import { FontsContext } from "@/react/story/providers/FontsContext";
import { useItems } from "@/react/story/providers/ItemsContext";
import { useStory } from "@/react/story/providers/StoryContext";
import type { Font } from "@/react/story/types";
import TemplatingUtils from "@/react/story/utils/TemplatingUtils";
import { type ReactNode, useMemo } from "react";

type Props = {
    children: ReactNode;
}

export default function FontsProvider(props: Props) {
    const Story = useStory();
    const Items = useItems();

    // Mémorisé : `?? []` rendrait un tableau neuf à chaque rendu, et `all` est une
    // dépendance du `useMemo` du contexte — qui serait alors recalculé à chaque fois.
    const all = useMemo(() => Story.story.fonts ?? [], [ Story.story.fonts ]);

    const context = useMemo(() => ({
        all,
        count: all.length,
        set: async (fonts: Font[]) => {
            const orderId1 = all.map(f => f.uid).join("/");
            const orderId2 = fonts.map(f => f.uid).join("/");
            if (orderId1 !== orderId2) {
                // Copies : la liste vient du composant de tri, qui garde les objets qu'il
                // nous passe — les renuméroter en place modifierait son état sous lui.
                return Story.patch("fonts", fonts.map((f, i) => ({ ...f, idx: i })));
            }

            return Promise.resolve();
        },
        save: async (font: Font) => {
            let fonts: Font[];
            if (all.find(f => f.uid === font.uid)) {
                fonts = all.map(f => f.uid === font.uid ? font : f);
            } else {
                fonts = [ ...all, font ];
            }
            return Story.patch("fonts", fonts);
        },
        remove: (font: Font) => {
            return Story.patch("fonts", all.filter(f => f.uid !== font.uid));
        },
        getStyleNode: (): ReactNode => {
            let fonts = TemplatingUtils.getFontFaces(Story.story);
            Items.getNavs().forEach(nav => {
                if (nav.story) {
                    fonts = new Map([ ...fonts, ...TemplatingUtils.getFontFaces(nav.story) ]);
                }
            });

            const styles: string[] = [];
            let isFirst: boolean = true;
            fonts.forEach((font, family) => {
                styles.push(font);

                if (isFirst) {
                    const defaultFont = `
          #story-viewer-container {
            font-family: "${family}", "Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
          }
          `;

                    styles.push(defaultFont);

                    isFirst = false;
                }
            });

            return (
                <style>
                    {styles.join("\n")}
                </style>
            );
        }
    }), [ all, Story, Items ]);

    return (
        <FontsContext.Provider value={context}>
            {props.children}
        </FontsContext.Provider>
    );
}
